<template>
  <div class="preference-settings">
    <v-card :class="`card-container ${darkModePref ? 'dark-border' : 'light-border'}`" flat>
      <h2>{{ $t('preferences.prefTitle') }}</h2>

      <v-list>
        <div class="general-prefs mt-4 mb-3">
          <v-flex xs12>
            <h4>
              {{ $t('preferences.generalPrefs') }}
            </h4>
          </v-flex>
          <v-divider></v-divider>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>{{ $t('preferences.darkMode') }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-switch
                v-if="getMe.preferences"
                v-model="darkModePref"
                :disabled="updating"
                color="primary"
                @change="update()"
              ></v-switch>
              <v-switch
                v-else
                v-model="darkModePref"
                :disabled="updating"
                color="primary"
                @change="update()"
              ></v-switch>
            </v-list-item-action>
          </v-list-item>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>{{ $t('preferences.deleteCache') }}</v-list-item-title>
            </v-list-item-content>
            <transition name="fade" appear>
              <v-list-item-action>
                <v-btn
                  small
                  text
                  color="primary"
                  @click="confirmDialog = true"
                  v-if="!loadingDeleteCache"
                >
                  {{ $tc('general.delete', 1) }}
                </v-btn>
                <v-btn small text icon class="mr-6" v-else>
                  <v-icon color="success">check</v-icon>
                  {{ $tc('general.delete', 2) }}
                </v-btn>
              </v-list-item-action>
            </transition>
          </v-list-item>
        </div>

        <div class="user-prefs mt-4 mb-3">
          <v-flex xs12>
            <h4>
              {{ $t('preferences.userDataPrefs') }}
            </h4>
          </v-flex>
          <v-divider></v-divider>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>{{ $t('preferences.hcpReset') }}</v-list-item-title>
            </v-list-item-content>
            <transition name="fade" appear>
              <v-list-item-action>
                <v-btn
                  small
                  text
                  color="primary"
                  @click="confirmHandicapResetDialog = true"
                  v-if="!loadingHCPReset"
                >
                  {{ $t('general.reset') }}
                </v-btn>
                <v-btn small text icon class="mr-6" v-else>
                  <v-icon color="success">check</v-icon>
                  {{ $t('general.reset') }}
                </v-btn>
              </v-list-item-action>
            </transition>
          </v-list-item>
        </div>

        <div class="notification-prefs mt-4 mb-3">
          <v-flex xs12>
            <h4>
              {{ $t('preferences.emailPrefs') }}
            </h4>
          </v-flex>
          <v-divider></v-divider>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>{{ $t('preferences.statsEmailHeader') }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-radio-group
                row
                mandatory
                v-model="statsEmailOptIn"
                class="pr-0"
                @change="update()"
              >
                <v-radio
                  v-for="e in statsEmailOptions"
                  :key="e.key"
                  :value="e.value"
                  :label="$t(`general.${e.key}`)"
                  color="primary"
                ></v-radio>
              </v-radio-group>
            </v-list-item-action>
          </v-list-item>
        </div>

        <div class="clubhouse-prefs mb-3">
          <v-flex xs12>
            <h4>
              {{ $t('preferences.clubhousePrefs') }}
            </h4>
          </v-flex>
          <v-divider></v-divider>

          <div class="progress mt-4 pt-4" v-if="loadingClubPref">
            <pulse-loader :color="$options.loaderColor" :size="$options.loaderSize"></pulse-loader>
          </div>

          <div v-else>
            <v-list-item v-for="item in memberClubs" :key="item.id">
              <v-list-item-content>
                <v-list-item-title>{{ item.clubhouse_name }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-radio-group row v-model="item.email_opt_in" mandatory class="pr-0">
                  <v-radio
                    v-for="e in $options.clubEmailOptions"
                    :key="e.key"
                    :value="e.value"
                    :label="$t(`general.${e.key}`)"
                    color="primary"
                    @change="updateClubEmailOption(item, e.value)"
                  ></v-radio>
                </v-radio-group>
              </v-list-item-action>
            </v-list-item>
          </div>
        </div>

        <!-- Apex preferences -->
        <div
          class="apex_player_prefs mt-4 mb-6"
          v-if="me && me.apex_preferences && getMe && getMe.apex_preferences"
        >
          <v-flex xs12>
            <h4>
              APEX Player Settings
              <v-icon class="pointer" small @click="refreshSettings">refresh</v-icon>
            </h4>
          </v-flex>

          <v-divider class="mb-6"></v-divider>

          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header> Player Settings </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Tee Box Color Preference</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="getMe.apex_preferences.tee_color"
                      label="Tee Color"
                      :items="teeOptions"
                      item-text="name"
                      item-value="value"
                      hide-details
                      @change="updateApexPrefs"
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Skill Level</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="getMe.apex_preferences.skill_level"
                      :items="skillOptions"
                      label="Skill Level"
                      item-text="name"
                      item-value="value"
                      hide-details
                      @change="updateApexPrefs"
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header> Ball and Camera </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Main View Tracer Style</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="getMe.apex_preferences.main_tracer_style"
                      label="Select a ball tracer style for your main view"
                      :items="mainTracerOptions"
                      item-text="name"
                      item-value="value"
                      hide-details
                      @change="updateApexPrefs"
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Top View Tracer</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="getMe.apex_preferences.top_view_tracer_style"
                      label="Select a ball tracer style for your top view"
                      :items="topViewTracerStyleOptions"
                      item-text="name"
                      item-value="value"
                      hide-details
                      @change="updateApexPrefs"
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Main View Tracers</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="getMe.apex_preferences.top_view_tracer"
                      label="Select a ball tracer style for your main view"
                      :items="topViewTracerOptions"
                      item-text="name"
                      item-value="value"
                      hide-details
                      @change="updateApexPrefs"
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Camera Options</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="getMe.apex_preferences.camera_type"
                      label="Select your camera option"
                      :items="cameraOptions"
                      item-text="name"
                      item-value="value"
                      hide-details
                      @change="updateApexPrefs"
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Ball Scaling</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="getMe.apex_preferences.ball_scaling"
                      label="Select the size of how you want the ball to appear when viewing your shot"
                      :items="ballScalingOptions"
                      item-text="name"
                      item-value="value"
                      hide-details
                      @change="updateApexPrefs"
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Putting Line</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="getMe.apex_preferences.putting_visual"
                      label="Select how you want the system to orient while putting"
                      :items="puttingLineOptions"
                      item-text="name"
                      item-value="value"
                      hide-details
                      @change="updateApexPrefs"
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Ball Indicator</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="getMe.apex_preferences.ball_indicator"
                      label="Select the visibility of the ball indicator"
                      :items="ballIndicatorOptions"
                      item-text="name"
                      item-value="value"
                      hide-details
                      @change="updateApexPrefs"
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Ball Indicator Distance</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="getMe.apex_preferences.ball_indicator_distance"
                      label="Distance Options"
                      :items="ballDistanceOptions"
                      item-text="name"
                      item-value="value"
                      hide-details
                      @change="updateApexPrefs"
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Pin Indicator</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="getMe.apex_preferences.pin_indicator"
                      label="Select the visibility of the pin indicator"
                      :items="pinIndicatorOptions"
                      item-text="name"
                      item-value="value"
                      hide-details
                      @change="updateApexPrefs"
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
              <v-expansion-panel-content>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Terrain Grid</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select
                      v-model="getMe.apex_preferences.terrain_grid"
                      label="Toggle the visibility of the terrain grid"
                      :items="terrainGridOptions"
                      item-text="name"
                      item-value="value"
                      hide-details
                      @change="updateApexPrefs"
                    ></v-select>
                  </v-list-item-action>
                </v-list-item>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
        <!-- END APEX Preferences section -->
      </v-list>
    </v-card>

    <SettingsBottomNav :limitedUser="limitedUser" />

    <v-dialog v-model="confirmDialog" transition="dialog-bottom-transition" width="650">
      <v-card :dark="$vuetify.theme.dark ? true : false">
        <v-card-title class="text-h5 primary white--text" primary-title>
          {{ $t('preferences.deleteCacheHeading') }}
        </v-card-title>

        <v-card-text class="text-body-1 py-6 font-weight-bold">
          {{ $t('preferences.deleteCacheMsg') }}
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" text @click.stop="cancelDeleteCache">
            {{ $t('general.no') }}
          </v-btn>
          <v-btn color="primary" text @click.stop="confirmDeleteCache">
            {{ $t('general.yes') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="confirmHandicapResetDialog"
      transition="dialog-bottom-transition"
      width="650"
    >
      <v-card :dark="$vuetify.theme.dark ? true : false">
        <v-card-title class="text-h5 primary white--text" primary-title>
          {{ $t('preferences.hcpResetHeading') }}
        </v-card-title>

        <v-card-text class="text-body-1 py-6 font-weight-bold">
          {{ $t('preferences.hcpResetMsg') }}
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" text @click.stop="cancelHCPReset">
            {{ $t('general.no') }}
          </v-btn>
          <v-btn color="primary" text @click.stop="confirmHCPReset">
            {{ $t('general.yes') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import meMixin from '@/mixins/meMixin';
import SettingsBottomNav from '@/components/SettingsBottomNav';
import { deleteCache } from '@/utils/axios';
import { loaderColor, loaderSizeXS, clubEmailOptions } from '@/lib/constants';

export default {
  name: 'PreferenceSettings',
  mixins: [meMixin],

  props: {
    limitedUser: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    SettingsBottomNav,
  },

  data() {
    return {
      darkModePref: false,

      updating: false,
      updatingClubPrefs: false,
      loadingClubPref: false,
      confirmDialog: false,
      confirmHandicapResetDialog: false,
      loadingDeleteCache: false,
      loadingHCPReset: false,
      statsEmailOptIn: true,
      statsEmailOptions: [
        { key: 'no', value: false },
        { key: 'yes', value: true },
      ],

      teeOptions: [
        { name: 'Black', value: 'black' },
        { name: 'Blue', value: 'blue' },
        { name: 'White', value: 'white' },
        { name: 'Red', value: 'red' },
        { name: 'Yellow', value: 'yellow' },
      ],

      skillOptions: [
        { name: 'Beginner', value: 'beginner' },
        { name: 'Advanced', value: 'advanced' },
        { name: 'Expert', value: 'expert' },
      ],

      mainTracerOptions: [
        { name: 'Comet', value: 'comet' },
        { name: 'Bold', value: 'bold' },
        { name: 'Thin', value: 'thin' },
        { name: 'Off', value: 'off' },
      ],

      topViewTracerStyleOptions: [
        { name: 'Thin', value: 'thin' },
        { name: 'Bold', value: 'bold' },
        { name: 'Off', value: 'off' },
      ],

      topViewTracerOptions: [
        { name: 'Single', value: 'current' },
        { name: 'All', value: 'all' },
        { name: 'Off', value: 'off' },
      ],

      cameraOptions: [
        { name: 'Stationary', value: 'static' },
        { name: 'TV Cameras', value: 'ball_follow' },
        { name: 'Follow Ball', value: 'stationary' },
      ],

      ballScalingOptions: [
        { name: 'Classic', value: 'small' },
        { name: 'Medium', value: 'medium' },
        { name: 'Large', value: 'large' },
      ],

      puttingLineOptions: [
        { name: 'Solid', value: 'solid' },
        { name: 'Dotted', value: 'dots' },
        { name: 'Off', value: 'off' },
      ],

      ballIndicatorOptions: [
        { name: 'Always On', value: 'always_on' },
        { name: 'Auto', value: 'auto' },
        { name: 'Off', value: 'off' },
      ],

      ballDistanceOptions: [
        { name: 'Distance Traveled', value: 'total_distance' },
        { name: 'Distance To Pin', value: 'distance_to_pin' },
      ],

      pinIndicatorOptions: [
        { name: 'Auto', value: 'auto' },
        { name: 'Always On', value: 'always_on' },
        { name: 'Off', value: 'off' },
      ],

      terrainGridOptions: [
        { name: 'On', value: 'on' },
        { name: 'Off', value: 'off' },
      ],

      woodIronMeasurementOptions: [
        { name: 'Imperial', value: 'yards_feet_inches' },
        { name: 'Metric', value: 'meters_centimeters' },
      ],

      puttingMeasurementOptions: [
        { name: 'Feet', value: 'feet' },
        { name: 'Yards', value: 'yards' },
      ],

      elevationOptions: [
        { name: 'Feet', value: 'feet' },
        { name: 'Yards', value: 'yards' },
      ],
    };
  },

  created() {
    //* non-reactive constants
    this.$options.loaderColor = loaderColor;
    this.$options.loaderSize = loaderSizeXS;
    this.$options.clubEmailOptions = clubEmailOptions;
  },

  mounted() {
    this.loadingClubPref = true;
    if (this.getMe?.preferences && this.getMe?.preferences?.darkMode) {
      this.darkModePref = this.getMe?.preferences?.darkMode ?? false;
      this.statsEmailOptIn = !this.getMe?.performanceEmailOptOut;
      this.$store.dispatch('clubhouses/listMemberClubs', this.getMe?.id).then((res) => {
        this.loadingClubPref = false;
      });
    } else {
      this.$store.dispatch('users/me').then((res) => {
        this.darkModePref = res?.preferences?.darkMode ?? false;
        this.statsEmailOptIn = !res?.performanceEmailOptOut;
        this.$store.dispatch('clubhouses/listMemberClubs', res.id).then((res) => {
          this.loadingClubPref = false;
        });
      });
    }
  },

  computed: {
    ...mapGetters({
      loading: 'clubhouses/loading',
      memberClubs: 'clubhouses/memberClubs',
    }),
  },

  methods: {
    update() {
      this.updating = true;
      const payload = {};

      payload.preferences = { ...this.getMe.preferences, darkMode: this.darkModePref };
      payload.performanceEmailOptOut = !this.statsEmailOptIn;

      this.$store
        .dispatch('users/update', payload)
        .then(() => {
          this.$vuetify.theme.dark = this.darkModePref;
          this.updating = false;
          this.$root.vtoast.show({
            text: `Your preferences have been updated`,
            color: 'success',
          });
        })
        .catch((err) => {
          try {
            const {
              data: { error = {} },
            } = err;

            this.$root.vtoast.show({
              text: `Error ${error.code}: ${error.msg}`,
              color: 'error',
            });
          } catch (error) {
            this.$root.vtoast.show({
              text: `Error updating preferences`,
              color: 'error',
            });
          }
        });
    },

    updateApexPrefs() {
      this.updating = true;
      const payload = {};
      localStorage.setItem('userUpdated', true);

      // payload.apex_preferences = { ...this.me.apex_preferences };
      payload.apex_preferences = { ...this.getMe.apex_preferences };

      console.log('pref payload:', payload);
      this.$store
        .dispatch('users/update', payload)
        .then(() => {
          this.updating = false;
          this.$root.vtoast.show({
            text: `Preferences saved`,
            color: 'success',
            timeout: 2000,
          });
        })
        .catch((err) => {
          this.updating = false;
          try {
            const {
              data: { error = {} },
            } = err;

            this.$root.vtoast.show({
              text: `Error ${error.code}: ${error.msg}`,
              color: 'error',
            });
          } catch (error) {
            this.$root.vtoast.show({
              text: `Error updating preferences`,
              color: 'error',
            });
          }
        });
    },

    updateClubEmailOption(item, event) {
      this.updatingClubPrefs = true;

      const { user_uuid, club_uuid } = item;
      const payload = {
        user_uuid,
        club_uuid,
        email_opt_in: event,
      };
      // console.log('payload:', payload);
      this.$store
        .dispatch('clubhouses/updateClubmember', payload)
        .then((res) => {
          // console.log('res:', res);
          this.$store.dispatch('clubhouses/listMemberClubs', res.user_uuid);
          this.updatingClubPrefs = false;
          this.$root.vtoast.show({
            text: `Preferences updated`,
            color: 'success',
          });
        })
        .catch(() => {
          this.updatingClubPrefs = false;
          this.$root.vtoast.show({
            text: `Error updating preferences`,
            color: 'error',
          });
        });
    },

    cancelDeleteCache() {
      this.confirmDialog = false;
      this.loadingDeleteCache = false;
    },

    confirmDeleteCache() {
      this.loadingDeleteCache = true;
      deleteCache();
      this.confirmDialog = false;
      setTimeout(() => {
        this.loadingDeleteCache = false;
      }, 3000);
    },

    cancelHCPReset() {
      this.confirmHandicapResetDialog = false;
      this.loadingHCPReset = false;
    },

    confirmHCPReset() {
      this.loadingHCPReset = true;
      const { id = '' } = this.getMe ?? {};
      if (!id) return;

      //* Call to reset handicap
      this.$store.dispatch('users/resetUserHandicap', id);
      this.confirmHandicapResetDialog = false;
      setTimeout(() => {
        this.loadingHCPReset = false;
      }, 3000);
    },

    refreshSettings() {
      this.$store.dispatch('users/me', { bypass: true }).catch((err) => {
        console.error(err);
      });
    },
  },
};
</script>

<style scoped>
.preference-settings {
  margin-top: -65px;
  min-height: calc(60vh - 65px) !important;
}

.preference-settings .card-container {
  padding: 30px !important;
  border-radius: 10px;
  border-radius: var(--border-10);
}

.submit {
  margin-top: 24px;
}

.apex_player_prefs * >>> .v-list-item__content {
  max-width: 50%;
}

.apex_player_prefs * >>> .v-list-item__action {
  min-width: 50%;
  max-width: 50%;
}

@media (max-width: 600px) {
  .preference-settings {
    margin-top: 0px;
  }

  * >>> .v-list-item {
    padding-left: 0;
  }

  .apex_player_prefs * >>> .v-list-item__content {
    max-width: 50%;
  }

  .apex_player_prefs * >>> .v-list-item__action {
    max-width: 50%;
  }
}
</style>
